import { api } from "./client";

/** Superficie de la API agrupada por dominio. Las pantallas usan solo esto. */

/** Modo mantenimiento de la lectura con Atlas. */
export const maintenanceApi = {
  status: () => api.get("/maintenance", { retry: false }),
  overview: () => api.get("/maintenance/admin", { retry: false }),
  set: (body) => api.put("/maintenance/admin", body),
};

export const authApi = {
  me: () => api.get("/auth/me"),
  providers: () => api.get("/auth/providers"),
  signIn: (payload) => api.post("/auth/login", payload),
  pendingVerification: () => api.get("/auth/2fa/pending", { retry: false }),
  verifySignIn: (payload) => api.post("/auth/2fa/verify", payload),
  resendVerification: (challengeId) => api.post("/auth/2fa/resend", { challengeId }),
  revokeTrustedDevices: () => api.delete("/auth/trusted-devices"),
  signOut: () => api.post("/auth/logout"),
  register: (payload) => api.post("/auth/register", payload),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (payload) => api.post("/auth/reset-password", payload),
};

export const requestsApi = {
  stockCustomerOrders: (ref, lineId) => api.post(`/requests/${encodeURIComponent(ref)}/lines/${encodeURIComponent(lineId)}/customer-orders`, {}, { retry: false }),
  pollStockCustomerOrders: (ref, lineId, checkId) => api.get(`/requests/${encodeURIComponent(ref)}/lines/${encodeURIComponent(lineId)}/customer-orders/${encodeURIComponent(checkId)}`, { retry: false }),
  valuation: ref => api.get(`/requests/${encodeURIComponent(ref)}/valuation`, { retry: false }),
  requestValuation: (ref, payload = {}) => api.post(`/requests/${encodeURIComponent(ref)}/valuation`, payload, { retry: false }),
  decideValuationAdditionalLines: (ref, payload) => api.post(`/requests/${encodeURIComponent(ref)}/valuation/additional-lines`, payload, { retry: false }),
  reviewValuation: (ref, payload) => api.post(`/requests/${encodeURIComponent(ref)}/valuation/review`, payload, { retry: false }),
  placeOrder: ref => api.post(`/requests/${encodeURIComponent(ref)}/order`, {}, { retry: false }),
  customerOrder: ref => api.get(`/requests/${encodeURIComponent(ref)}/order`, { retry: false }),
  readingConnection: (ref) => api.get(`/requests/${encodeURIComponent(ref)}/reading-connection`, { retry: false }),
  setReadingConnection: (ref, connection) => api.put(`/requests/${encodeURIComponent(ref)}/reading-connection`, { connection }, { retry: false }),
  uploadInstallationReferences: (ref, formData) => api.upload(`/requests/${encodeURIComponent(ref)}/installation/references`, formData, { retry: false }),
  deleteInstallationReference: (ref, fileId, expectedUpdatedAt) => api.delete(`/requests/${encodeURIComponent(ref)}/installation/references/${encodeURIComponent(fileId)}?expectedUpdatedAt=${encodeURIComponent(expectedUpdatedAt)}`, { retry: false }),
  startInstallationPlan: (ref, payload) => api.post(`/requests/${encodeURIComponent(ref)}/installation`, payload, { retry: false }),
  installationPlan: (ref) => api.get(`/requests/${encodeURIComponent(ref)}/installation`, { retry: false }),
  resolveInstallationPlan: (ref, payload) => api.post(`/requests/${encodeURIComponent(ref)}/installation/resolution`, payload, { retry: false }),
  retryInstallationPlan: (ref, payload) => api.post(`/requests/${encodeURIComponent(ref)}/installation/retry`, payload, { retry: false }),
  answerInstallationPlan: (ref, payload) => api.post(`/requests/${encodeURIComponent(ref)}/installation/answers`, payload, { retry: false }),
  adoptInstallationPlan: (ref, payload) => api.put(`/requests/${encodeURIComponent(ref)}/installation/materials`, payload, { retry: false }),
  clearInstallationPlan: (ref, payload) => api.delete(`/requests/${encodeURIComponent(ref)}/installation`, { retry: false, ...(payload ? { body: payload } : {}) }),
  verifyInstallation: (ref, payload = {}) => api.post(`/requests/${encodeURIComponent(ref)}/installation/verification`, payload, { retry: false }),
  answerInstallationVerification: (ref, payload) => api.post(`/requests/${encodeURIComponent(ref)}/installation/verification/answers`, payload, { retry: false }),
  saveInstallationPreview: (ref, payload) => api.put(`/requests/${encodeURIComponent(ref)}/installation-preview`, payload),
  installationEstimate: (ref) => api.get(`/requests/${encodeURIComponent(ref)}/installation-preview/estimate`),
  clearInstallationPreview: (ref) => api.delete(`/requests/${encodeURIComponent(ref)}/installation-preview`),
  list: (params = {}) => api.get(`/requests${toQuery(params)}`),
  draft: () => api.get("/requests/draft"),
  get: (ref) => api.get(`/requests/${encodeURIComponent(ref)}`),
  create: (payload) => api.post("/requests", payload),
  update: (ref, payload) =>
    api.patch(`/requests/${encodeURIComponent(ref)}`, payload),
  cancel: (ref) => api.post(`/requests/${encodeURIComponent(ref)}/cancel`),
  undoCancellation: (ref) =>
    api.post(`/requests/${encodeURIComponent(ref)}/cancel/undo`),
  /**
   * Borra definitivamente un borrador o una solicitud ya cancelada. Una
   * solicitud activa debe pasar primero por `cancel`, para conservar el plazo
   * de recuperación y no saltarse accidentalmente la trazabilidad.
   */
  remove: (ref) => api.delete(`/requests/${encodeURIComponent(ref)}`),
  uploadFiles: (ref, formData) =>
    api.upload(`/requests/${encodeURIComponent(ref)}/files`, formData),
  deleteFile: (ref, fileId) =>
    api.delete(`/requests/${encodeURIComponent(ref)}/files/${fileId}`),
  /**
   * Cómo va el reconocimiento.
   *
   * No es un `get` con otro nombre: esta llamada es la que EMPUJA el trabajo.
   * El portal no vigila en segundo plano; pregunta a Atlas cuando alguien
   * está mirando la pantalla, y es en esa misma respuesta cuando aterrizan
   * las líneas reconocidas.
   */
  recognition: (ref) =>
    api.get(`/requests/${encodeURIComponent(ref)}/recognition`, {
      // Aunque sea GET, esta llamada puede iniciar trabajo en Atlas. La capa
      // HTTP no debe repetirla a espaldas de la pantalla.
      retry: false,
    }),
  stock: (ref) => api.get(`/requests/${encodeURIComponent(ref)}/stock`),
  refreshStock: (ref) => api.post(`/requests/${encodeURIComponent(ref)}/stock`, {}, { retry: false }),
  answerRecognitionQuestions: (ref, payload) =>
    api.post(
      `/requests/${encodeURIComponent(ref)}/recognition/answers`,
      payload,
    ),
  confirmVisualReview: (ref, payload) =>
    api.post(
      `/requests/${encodeURIComponent(ref)}/recognition/visual-review`,
      payload,
      ),
  confirmDocumentReview: (ref, payload) =>
    api.post(`/requests/${encodeURIComponent(ref)}/recognition/document-review`, payload, { retry: false }),
  recommendationAnalysis: (ref) =>
    api.get(`/requests/${encodeURIComponent(ref)}/recommendations`),
  startRecommendationAnalysis: (ref, payload = {}) =>
    api.post(
      `/requests/${encodeURIComponent(ref)}/recommendations/analyze`,
      payload,
    ),
  answerRecommendationQuestions: (ref, payload) =>
    api.post(
      `/requests/${encodeURIComponent(ref)}/recommendations/answers`,
      payload,
    ),
  decideRecommendation: (ref, recommendationId, decision) =>
    api.post(
      `/requests/${encodeURIComponent(ref)}/recommendations/${encodeURIComponent(recommendationId)}/decision`,
      { decision },
    ),
  replaceLines: (ref, lines) =>
    api.put(`/requests/${encodeURIComponent(ref)}/lines`, { lines }),
  addLine: (ref, line) =>
    api.post(`/requests/${encodeURIComponent(ref)}/lines`, line),
  updateLine: (ref, lineId, patch) =>
    api.patch(`/requests/${encodeURIComponent(ref)}/lines/${lineId}`, patch),
  confirmUnit: (ref, lineId, payload) =>
    api.post(`/requests/${encodeURIComponent(ref)}/lines/${encodeURIComponent(lineId)}/confirm-unit`, payload),
  confirmEquivalence: (ref, lineId) =>
    api.post(
      `/requests/${encodeURIComponent(ref)}/lines/${lineId}/confirm-equivalence`,
    ),
  deleteLine: (ref, lineId) =>
    api.delete(`/requests/${encodeURIComponent(ref)}/lines/${lineId}`),
  startProcessing: (ref) =>
    api.post(`/requests/${encodeURIComponent(ref)}/process`),
  attachManualLinesFile: (ref) =>
    api.post(`/requests/${encodeURIComponent(ref)}/manual-lines/file`),
  // «Describe tu instalación» read first by Atlas, where its connection supports it.
  typedTextCapability: (ref) =>
    api.get(`/requests/${encodeURIComponent(ref)}/typed-text`, { retry: false }),
  attachTypedText: (ref, text) =>
    api.post(`/requests/${encodeURIComponent(ref)}/typed-text/file`, { text }, { retry: false }),
  markReviewable: (ref) =>
    api.post(`/requests/${encodeURIComponent(ref)}/review`),
  submit: (ref) => api.post(`/requests/${encodeURIComponent(ref)}/submit`),
  retrySubmission: (ref) => api.post(`/requests/${encodeURIComponent(ref)}/retry-submission`),
  recoverReference: (ref) => api.post(`/requests/${encodeURIComponent(ref)}/recover-reference`),
};

export const budgetsApi = {
  list: (params = {}) => api.get(`/budgets${toQuery(params)}`),
  sync: () => api.post("/budgets/sync"),
  get: (ref) => api.get(`/budgets/${encodeURIComponent(ref)}`),
  approve: (ref) => api.post(`/budgets/${encodeURIComponent(ref)}/approve`),
  reject: (ref, reason) =>
    api.post(`/budgets/${encodeURIComponent(ref)}/reject`, { reason }),
  /** Devuelve la propuesta a Adaltra para que la corrija, sin rechazarla. */
  requestChanges: (ref, note) =>
    api.post(`/budgets/${encodeURIComponent(ref)}/request-changes`, { note }),
};

  export const notificationsApi = {
    list: () => api.get("/notifications"),
    clear: () => api.delete("/notifications"),
    markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post("/notifications/read-all"),
  pushConfig: () => api.get("/notifications/push/config"),
  subscribePush: (subscription) =>
    api.post("/notifications/push/subscribe", subscription),
  unsubscribePush: (endpoint) =>
    api.post("/notifications/push/unsubscribe", { endpoint }),
};

export const profileApi = {
  notificationPreferences: () => api.get("/profile/notification-preferences"),
  updateNotificationPreferences: (preferences) => api.patch("/profile/notification-preferences", { preferences }),
  update: (payload) => api.patch("/profile", payload),
  updateCompany: (payload) => api.patch("/profile/company", payload),
  changePassword: (payload) => api.post("/profile/password", payload),
  sendSupport: (payload) => api.post("/profile/support", payload),
};

export const assistantApi = {
  status: () => api.get("/assistant/status"),
  /** Índice de conversaciones, la más reciente primero. */
  conversations: () => api.get("/assistant/conversations"),
  /** Abre una conversación nueva, o reutiliza la que esté sin estrenar. */
  createConversation: () => api.post("/assistant/conversations"),
  conversation: (id) =>
    api.get(`/assistant/conversations/${encodeURIComponent(id)}`),
  // Una respuesta puede tardar más de un minuto: quien la pide tiene que
  // poder desistir, así que la llamada admite una señal de cancelación.
  send: (id, text, locale, signal) =>
    api.post(
      `/assistant/conversations/${encodeURIComponent(id)}/message`,
      { text, locale },
      { signal },
    ),
  /**
   * Igual que `send`, pero contando lo que va pasando: primero en qué está
   * pensando Atlas y después la respuesta.
   *
   * Si el flujo no se puede abrir —el servicio del asistente sin reiniciar, un
   * proxy que no deja pasar `text/event-stream`—, quien llama puede recaer en
   * `send`, que sigue existiendo y no se ha tocado.
   */
  sendStream: (id, text, { locale, signal, onEvento } = {}) =>
    api.stream(
      `/assistant/conversations/${encodeURIComponent(id)}/message/stream`,
      { text, locale },
      { signal, onEvento },
    ),
  rename: (id, title) =>
    api.patch(`/assistant/conversations/${encodeURIComponent(id)}`, { title }),
  removeConversation: (id) =>
    api.delete(`/assistant/conversations/${encodeURIComponent(id)}`),
};

export const adminApi = {
  pendingRegistrationCount: () => api.get("/admin/registrations/count"),
  overview: () => api.get("/admin/overview"),
  settings: () => api.get("/admin/settings"),
  updateSettings: (payload) => api.patch("/admin/settings", payload),
  registrations: (status = "pending") =>
    api.get(`/admin/registrations${toQuery({ status })}`),
  users: () => api.get("/admin/users"),
  userRequests: (id) =>
    api.get(`/admin/users/${encodeURIComponent(id)}/requests`),
  requestDetail: (ref) =>
    api.get(`/admin/requests/${encodeURIComponent(ref)}`),
  dispatchRequest: (ref) => api.post(`/admin/requests/${encodeURIComponent(ref)}/dispatch`),
  reanalyzeRequest: (ref) => api.post(`/admin/request-workflow/requests/${encodeURIComponent(ref)}/process`, {}),
  cancelRequest: (ref) => api.post(`/admin/request-workflow/requests/${encodeURIComponent(ref)}/cancel`),
  restoreRequest: (ref) => api.post(`/admin/request-workflow/requests/${encodeURIComponent(ref)}/cancel/undo`),
  deleteRequest: (ref) => api.delete(`/admin/request-workflow/requests/${encodeURIComponent(ref)}`),
  recoverRequest: (ref) => api.post(`/admin/requests/${encodeURIComponent(ref)}/recover-reference`),
  analyzeRequestRecommendations: (ref, payload = {}) => api.post(`/admin/requests/${encodeURIComponent(ref)}/recommendations/analyze`, payload),
  requestRecommendations: (ref) => api.get(`/admin/requests/${encodeURIComponent(ref)}/recommendations`),
  answerRequestRecommendations: (ref, payload) => api.post(`/admin/requests/${encodeURIComponent(ref)}/recommendations/answers`, payload),
  decideRequestRecommendation: (ref, id, decision) => api.post(`/admin/requests/${encodeURIComponent(ref)}/recommendations/${encodeURIComponent(id)}/decision`, { decision }),
  updateUser: (id, payload) =>
    api.patch(`/admin/users/${encodeURIComponent(id)}`, payload),
  forceUserPasswordReset: (id) => api.post(`/admin/users/${encodeURIComponent(id)}/force-password-reset`, {}, { retry: false }),
  deleteUser: (id) => api.delete(`/admin/users/${encodeURIComponent(id)}`, { retry: false }),
  approveRegistration: (id) => api.post(`/admin/registrations/${id}/approve`),
  rejectRegistration: (id) => api.post(`/admin/registrations/${id}/reject`),
  tickets: (status) => api.get(`/admin/tickets${toQuery({ status })}`),
  updateTicket: (id, status) => api.patch(`/admin/tickets/${id}`, { status }),
  audit: (limit = 60) => api.get(`/admin/audit${toQuery({ limit })}`),
  releases: () => api.get("/admin/releases"),
  createRelease: (payload) => api.post("/admin/releases", payload),
  updateRelease: (id, payload) =>
    api.patch(`/admin/releases/${encodeURIComponent(id)}`, payload),
  platformRequests: () => api.get("/admin/platform-requests"),
  createPlatformRequest: (payload) =>
    api.post("/admin/platform-requests", payload),
  updatePlatformRequest: (id, payload) =>
    api.patch(`/admin/platform-requests/${encodeURIComponent(id)}`, payload),
  assistantAnalytics: () => api.get("/admin/assistant/analytics"),
  testHermes: () => api.post("/admin/hermes/test"),
  atlasSkills: () => api.get("/admin/hermes/skills"),
  testAdaQuote: () => api.post("/admin/ada-quote/test"),
  testNotificationEmail: () => api.post("/admin/notifications/test-email"),
  testNotificationPush: () => api.post("/admin/notifications/test-push"),
  generateVapidKeys: () => api.post("/admin/notifications/vapid-keys"),
  notificationAudience: () => api.get("/admin/notifications/audience"),
  broadcastPush: (payload) =>
    api.post("/admin/notifications/broadcast-push", payload),
  broadcastEmail: (formData) =>
    api.upload("/admin/notifications/broadcast-email", formData),
  connections: (service) =>
    api.get(`/admin/connections${toQuery({ service })}`),
  catalog: () => api.get("/admin/catalog"),
  modules: () => api.get("/admin/modules"),
  syncCatalog: (force = false) =>
    api.post("/admin/catalog/sync", { force }),

  /* Presupuestos de todos los clientes. A diferencia de `budgetsApi`, estas
     no se acotan por empresa: son para el personal de Adaltra. */
  budgets: (params = {}) => api.get(`/admin/budgets${toQuery(params)}`),
  extendBudget: (ref, validUntil) =>
    api.post(`/admin/budgets/${encodeURIComponent(ref)}/extend`, { validUntil }),
  withdrawBudget: (ref) =>
    api.post(`/admin/budgets/${encodeURIComponent(ref)}/withdraw`),
  remindBudget: (ref) =>
    api.post(`/admin/budgets/${encodeURIComponent(ref)}/remind`),
};

export const customerDirectoryApi = {
  status: () => api.get("/customers/status", { retry: false }),
  search: params => api.get(`/customers/search${toQuery(params)}`, { retry: false }),
  addresses: (customerId, masterVersion) => api.get(`/customers/${encodeURIComponent(customerId)}/addresses${toQuery({ master_version: masterVersion })}`, { retry: false }),
};

export const catalogApi = {
  checkStock: (q) => api.post("/catalog/stock-checks", { q }, { retry: false }),
  pollStock: (id) => api.get(`/catalog/stock-checks/${encodeURIComponent(id)}`, { retry: false }),
  lookup: (params) => api.get(`/catalog/lookup${toQuery(params)}`),
  lookupEquivalence: (params) =>
    api.get(`/catalog/equivalences/lookup${toQuery(params)}`),
  searchEquivalences: (q, limit = 8) =>
    api.get(`/catalog/equivalences/search${toQuery({ q, limit })}`),
  search: (q, options = {}) =>
    api.get(`/catalog/search${toQuery({ q, ...options })}`),
  dashboard: () => api.get("/catalog/dashboard"),
};

function toQuery(params) {
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== "",
  );
  if (entries.length === 0) return "";
  return `?${new URLSearchParams(entries).toString()}`;
}
